/**
 * Product detail.
 *
 * The top half mirrors the store: full-bleed product shot on the pale ground
 * with a printed hangtag beside it. The bottom half is the part the store does
 * not give you — provenance, price history, per-size stock history, where the
 * product is placed, and everything the watcher has logged about it.
 *
 * The hangtag fields are rendered directly here rather than through the shared
 * Hangtag component, because this screen needs watcher-only affordances inside
 * the tag (size-level watch toggles) that the shop grid's tag does not have.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ApiError,
  createWatch,
  deleteWatch,
  getProduct,
  getStoredToken,
  getWatches,
  toMessage,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { money, relativeTime, resolveImage } from "../lib/format";
import { SOCKET_EVENTS, useSocketEvent } from "../lib/socket";
import type { Page, Placement, ProductDetail, ProductStatus, ProductUpdate, Watch } from "../lib/types";
import EventRow, { formatValue, fullStamp } from "../components/EventRow";
import StockTimeline from "../components/StockTimeline";

type Phase = "loading" | "ready" | "missing" | "error";

const STATUS_LABEL: Record<ProductStatus, string> = {
  live: "Live",
  "sold-out": "Sold out",
  unlisted: "Unlisted",
  "feed-only": "Feed only",
  gone: "Gone",
};

const STATUS_NOTE: Record<ProductStatus, string> = {
  live: "Listed on the store and buyable in at least one size right now.",
  "sold-out": "Still on the store, but every size is out of stock — this is the state a restock breaks.",
  unlisted:
    "Sitting in a page's markup but missing from the public products feed — usually a drop staged early or quietly pulled, which is exactly the kind of thing worth watching before anyone else sees it.",
  "feed-only":
    "Present in the public products feed but not linked from any page the watcher crawls — it exists, but nothing on the site points at it yet.",
  gone: "Absent from both the feed and every page's markup on the most recent run.",
};

const CSS = `
.awprod{
  --ink:#0D0D0D;--red:#FF0000;--pink:#E89AC7;--muted:#8A8A8A;--line:rgba(13,13,13,.14);
  max-width:1180px;margin:0 auto;padding:26px 20px 96px;
  font-family:"Sora","Helvetica Neue",Arial,sans-serif;color:var(--ink);
}
.awprod-back{
  display:inline-block;margin-bottom:26px;
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:11px;letter-spacing:.2em;text-transform:uppercase;
  color:var(--ink);text-decoration:none;
}
.awprod-back:hover{text-decoration:underline;text-underline-offset:4px}

.awprod-hero{display:grid;grid-template-columns:minmax(0,1fr) 190px;gap:48px;align-items:start}
.awprod-media{min-width:0}
.awprod-shot{
  display:block;width:100%;height:auto;max-height:76vh;object-fit:contain;
  background:transparent;
}
.awprod-noshot{
  display:grid;place-items:center;aspect-ratio:1/1;border:1px dashed var(--line);
  font-family:"Oswald","Arial Narrow",sans-serif;font-size:11px;letter-spacing:.2em;color:var(--muted);
}
.awprod-thumbs{display:flex;flex-wrap:wrap;gap:8px;margin-top:14px}
.awprod-thumb{
  width:54px;height:54px;padding:0;border:1px solid var(--line);background:#FFFFFF;cursor:pointer;
}
.awprod-thumb.is-on{border-color:var(--ink)}
.awprod-thumb img{width:100%;height:100%;object-fit:cover;display:block}

/* ---- hangtag ---- */
.awprod-tagwrap{position:sticky;top:24px;padding-top:40px}
.awprod-string{position:absolute;top:0;left:50%;transform:translateX(-50%);width:78px;height:44px;overflow:visible}
.awprod-tag{position:relative;background:#FFFFFF;padding:24px 15px 12px;text-align:center}
.awprod-hole{
  position:absolute;top:10px;left:50%;transform:translateX(-50%);
  width:9px;height:9px;border-radius:50%;background:#EDEDED;
  box-shadow:inset 0 0 0 1px rgba(13,13,13,.4);
}
.awprod-price{
  margin:0;font-family:"Anton","Arial Narrow","Helvetica Neue",Impact,sans-serif;
  font-size:18px;line-height:1;letter-spacing:.02em;
}
.awprod-name{
  margin:6px 0 0;font-family:"Anton","Arial Narrow","Helvetica Neue",Impact,sans-serif;
  font-size:24px;line-height:1.02;letter-spacing:.01em;text-transform:uppercase;overflow-wrap:anywhere;
}
.awprod-desc{margin:10px 0 0;padding:0;list-style:none}
.awprod-desc li{font-size:12px;line-height:1.5;text-transform:uppercase;letter-spacing:.04em}
.awprod-sizes{display:flex;flex-wrap:wrap;justify-content:center;gap:5px;margin:14px 0 0;padding:0;list-style:none}
.awprod-size{
  min-width:28px;padding:5px 4px;border:1px solid var(--line);background:transparent;
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:11px;line-height:1;letter-spacing:.08em;color:var(--ink);
}
button.awprod-size{cursor:pointer}
button.awprod-size:hover{border-color:var(--ink)}
.awprod-size.is-out{color:var(--muted);text-decoration:line-through;text-decoration-color:var(--red)}
.awprod-size.is-picked{box-shadow:inset 0 -3px 0 var(--pink);border-color:var(--ink)}
.awprod-guide{margin:12px 0 0;font-size:12px;letter-spacing:.04em;text-transform:uppercase;color:var(--muted)}
.awprod-soldout{
  margin:12px 0 0;font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:20px;line-height:1;letter-spacing:.06em;text-transform:uppercase;color:var(--red);
}
.awprod-watch{
  display:block;width:100%;margin:14px 0 0;padding:10px 6px;
  border:1px solid var(--ink);background:transparent;color:var(--ink);cursor:pointer;
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:11px;letter-spacing:.2em;text-transform:uppercase;
}
.awprod-watch:hover:not(:disabled){background:var(--ink);color:#FFFFFF}
.awprod-watch.is-on{background:var(--ink);color:#FFFFFF}
.awprod-watch.is-on:hover:not(:disabled){background:transparent;color:var(--ink)}
.awprod-watch:disabled{opacity:.45;cursor:default}
.awprod-hint{margin:7px 0 0;font-size:9px;line-height:1.5;letter-spacing:.06em;text-transform:uppercase;color:var(--muted)}
.awprod-hint.is-error{color:#8C1D1D}
.awprod-swirl{display:block;margin:14px auto 0;width:86px;height:24px}
.awprod-fine{margin:8px 0 0;font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--ink)}
.awprod-flash{
  display:inline-block;margin:10px 0 0;padding:3px 6px;border:1px solid var(--pink);
  font-family:"Oswald","Arial Narrow",sans-serif;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--ink);
  animation:awprod-fade 6s ease-out forwards;
}
@keyframes awprod-fade{0%{opacity:1}75%{opacity:1}100%{opacity:0}}

/* ---- status strip ---- */
.awprod-status{
  display:flex;align-items:baseline;gap:16px;flex-wrap:wrap;
  margin-top:56px;padding:18px 20px;background:#FFFFFF;border:1px solid var(--line);
}
.awprod-status.is-alert{border-color:var(--red)}
.awprod-statusword{
  font-family:"Anton","Arial Narrow","Helvetica Neue",Impact,sans-serif;
  font-size:22px;line-height:1;letter-spacing:.02em;text-transform:uppercase;white-space:nowrap;
}
.awprod-status.is-alert .awprod-statusword{color:var(--red)}
.awprod-statusnote{margin:0;font-size:12px;line-height:1.65;color:var(--muted);flex:1 1 320px;min-width:0}

/* ---- panels ---- */
/* Exactly two columns: the two narrow panels pair up and the wide ones span
   both, so auto-fit can never open an empty third cell. */
.awprod-panels{
  display:grid;grid-template-columns:repeat(2,minmax(0,1fr));
  gap:1px;background:var(--line);border:1px solid var(--line);margin-top:1px;
}
.awprod-panel{background:#FFFFFF;padding:22px;min-width:0}
.awprod-panel.is-wide{grid-column:1 / -1}
.awprod-panel.is-flush{padding:22px 0 0}
.awprod-panel h2{
  margin:0 0 14px;padding-bottom:10px;border-bottom:1px solid var(--line);
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:var(--muted);font-weight:500;
}
.awprod-panel.is-flush h2{margin:0 0 0;padding:0 22px 12px}

.awprod-facts{margin:0;display:grid;grid-template-columns:auto minmax(0,1fr);gap:8px 16px}
.awprod-facts dt{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);white-space:nowrap}
.awprod-facts dd{margin:0;font-size:12px;line-height:1.5;overflow-wrap:anywhere}
.awprod-facts dd small{display:block;color:var(--muted);font-size:10px;letter-spacing:.04em}

.awprod-rows{list-style:none;margin:0;padding:0}
.awprod-rows > li{padding:10px 0;border-bottom:1px solid var(--line);font-size:12px;line-height:1.5}
.awprod-rows > li:last-child{border-bottom:none}
.awprod-rowtop{display:flex;justify-content:space-between;gap:12px;align-items:baseline}
.awprod-rowsub{margin:3px 0 0;font-size:10.5px;color:var(--muted);letter-spacing:.03em;overflow-wrap:anywhere}
.awprod-rowtime{font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);white-space:nowrap}
.awprod-path{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11.5px;overflow-wrap:anywhere}
.awprod-flag{
  margin-left:8px;border:1px solid var(--red);color:var(--red);
  font-family:"Oswald","Arial Narrow",sans-serif;font-size:9px;letter-spacing:.12em;
  text-transform:uppercase;padding:2px 4px;white-space:nowrap;
}
.awprod-empty{margin:0;font-size:11.5px;line-height:1.65;color:var(--muted)}
.awprod-eventlist{list-style:none;margin:0;padding:0;border-top:1px solid var(--line)}

.awprod-note{
  max-width:520px;margin:80px auto;padding:44px 24px;background:#FFFFFF;border:1px solid var(--line);text-align:center;
}
.awprod-note h1{
  margin:0;font-family:"Anton","Arial Narrow","Helvetica Neue",Impact,sans-serif;
  font-size:24px;letter-spacing:.02em;text-transform:uppercase;
}
.awprod-note p{margin:12px 0 0;font-size:12px;line-height:1.7;color:var(--muted)}
.awprod-note a{color:var(--ink)}
.awprod-btn{
  margin-top:20px;padding:12px 26px;border:1px solid var(--ink);background:transparent;color:var(--ink);cursor:pointer;
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:11px;letter-spacing:.18em;text-transform:uppercase;
}
.awprod-btn:hover{background:var(--ink);color:#FFFFFF}

@media (max-width:880px){
  .awprod-hero{grid-template-columns:minmax(0,1fr);gap:26px}
  .awprod-tagwrap{position:static;width:190px;margin:0 auto;padding-top:40px}
}
@media (max-width:720px){
  .awprod-panels{grid-template-columns:minmax(0,1fr)}
}
@media (max-width:640px){
  .awprod{padding:20px 14px 80px}
  .awprod-panel{padding:18px}
  .awprod-panel.is-flush{padding:18px 0 0}
  .awprod-panel.is-flush h2{padding:0 18px 12px}
  .awprod-facts{grid-template-columns:minmax(0,1fr);gap:2px 0}
  .awprod-facts dd{margin-bottom:8px}
}
`;

const STYLE_ID = "aw-product-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const tag = document.createElement("style");
  tag.id = STYLE_ID;
  tag.textContent = CSS;
  document.head.appendChild(tag);
}

function TagString() {
  return (
    <svg className="awprod-string" viewBox="0 0 78 44" aria-hidden="true" focusable="false">
      <path
        d="M39 44 L39 30 C39 22 24 22 24 13 C24 5 33 2 39 2 C45 2 54 5 54 13 C54 22 39 22 39 30"
        fill="none"
        stroke="#0D0D0D"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Swirl() {
  return (
    <svg className="awprod-swirl" viewBox="0 0 120 34" aria-hidden="true" focusable="false">
      <path
        d="M4 24c8-14 16-18 22-10 5 7-6 16-10 10-5-8 10-20 22-20 10 0 12 14 20 14 7 0 9-8 16-8 6 0 8 6 14 6 5 0 8-3 12-8"
        fill="none"
        stroke="#E89AC7"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Fact({ label, iso, fallback = "never" }: { label: string; iso?: string; fallback?: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        {iso ? (
          <>
            {relativeTime(iso)}
            <small>{fullStamp(iso)}</small>
          </>
        ) : (
          fallback
        )}
      </dd>
    </>
  );
}

export function Product() {
  const { productId } = useParams<{ productId: string }>();
  const auth = useAuth();

  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState("");
  const [activeShot, setActiveShot] = useState(0);
  const [liveTick, setLiveTick] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [watch, setWatch] = useState<Watch | null>(null);
  const [pickedSizes, setPickedSizes] = useState<string[]>([]);
  const [watchBusy, setWatchBusy] = useState(false);
  const [watchError, setWatchError] = useState("");

  // A stored token is enough to offer the watch controls: the request itself
  // is the real authority, and it reports back through `watchError`.
  const [hasToken] = useState(() => getStoredToken() !== null);
  const canWatch = auth.authenticated || hasToken;

  const numericId = Number(productId);

  /* ---- load ---- */
  useEffect(() => {
    if (!productId || !Number.isFinite(numericId)) {
      setPhase("missing");
      return;
    }
    const controller = new AbortController();
    setPhase("loading");
    setError("");
    setActiveShot(0);
    setLiveTick(0);

    getProduct(numericId, controller.signal)
      .then((data) => {
        if (!data?.product) {
          setPhase("missing");
          return;
        }
        setDetail({
          product: data.product,
          variants: data.variants ?? [],
          events: data.events ?? [],
          pages: data.pages ?? [],
        });
        setPhase("ready");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.status === 404) {
          setPhase("missing");
          return;
        }
        setError(toMessage(err));
        setPhase("error");
      });

    return () => controller.abort();
  }, [productId, numericId, reloadNonce]);

  /* ---- live availability ---- */
  useSocketEvent<ProductUpdate>(SOCKET_EVENTS.productUpdated, (payload) => {
    if (!payload || payload.productId !== numericId) return;
    const sizes = Array.isArray(payload.availableSizes) ? payload.availableSizes : [];
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            product: {
              ...prev.product,
              available: payload.available,
              availableSizes: sizes,
              priceCents: payload.priceCents ?? prev.product.priceCents,
            },
            // `available` is a snapshot of now, so mirror it onto the variants
            // to keep the size chips and the stock timeline in agreement.
            variants: prev.variants.map((v) => ({ ...v, available: sizes.includes(v.title) })),
          }
        : prev,
    );
    setLiveTick((tick) => tick + 1);
  });

  /* ---- existing watch ---- */
  useEffect(() => {
    if (!canWatch || !Number.isFinite(numericId)) return;
    const controller = new AbortController();

    getWatches(controller.signal)
      .then((list) => {
        const mine = list.find((w) => w.productId === numericId && w.active);
        if (mine) {
          setWatch(mine);
          setPickedSizes(mine.sizes ?? []);
        }
      })
      .catch(() => {
        /* read-only fallback: a failed watch lookup must not break the page */
      });

    return () => controller.abort();
  }, [canWatch, numericId]);

  const product = detail?.product;

  const gallery = useMemo(() => {
    if (!product) return [] as string[];
    const list = product.images?.length ? product.images : product.featuredImage ? [product.featuredImage] : [];
    return list.map((src) => resolveImage(src)).filter((src): src is string => Boolean(src));
  }, [product]);

  const sizes = useMemo(() => {
    if (!detail) return [] as string[];
    if (detail.variants.length > 0) {
      const ordered = [...detail.variants].sort((a, b) => a.position - b.position);
      const seen = new Set<string>();
      const out: string[] = [];
      for (const variant of ordered) {
        if (variant.title && !seen.has(variant.title)) {
          seen.add(variant.title);
          out.push(variant.title);
        }
      }
      return out;
    }
    return detail.product.availableSizes ?? [];
  }, [detail]);

  const priceHistory = useMemo(
    () =>
      (detail?.events ?? [])
        .filter((e) => e.type === "price-change")
        .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt)),
    [detail],
  );

  const placements = useMemo(() => {
    if (!detail) return [] as Array<Placement & { page?: Page }>;
    const byPath = new Map(detail.pages.map((page) => [page.path, page]));
    return detail.product.sourcePages.map((placement) => ({
      ...placement,
      page: byPath.get(placement.path),
    }));
  }, [detail]);

  const restockTotal = useMemo(
    () => (detail?.variants ?? []).reduce((sum, v) => sum + (v.restockCount ?? 0), 0),
    [detail],
  );

  async function saveWatch(nextSizes: string[]) {
    if (!Number.isFinite(numericId)) return;
    setWatchBusy(true);
    setWatchError("");
    try {
      setWatch(await createWatch({ productId: numericId, sizes: nextSizes }));
    } catch (err) {
      setWatchError(toMessage(err));
    } finally {
      setWatchBusy(false);
    }
  }

  async function stopWatch() {
    if (!watch) return;
    setWatchBusy(true);
    setWatchError("");
    try {
      await deleteWatch(watch._id);
      setWatch(null);
    } catch (err) {
      setWatchError(toMessage(err));
    } finally {
      setWatchBusy(false);
    }
  }

  function toggleSize(size: string) {
    const next = pickedSizes.includes(size)
      ? pickedSizes.filter((s) => s !== size)
      : [...pickedSizes, size];
    setPickedSizes(next);
    if (watch) void saveWatch(next);
  }

  /* ---- non-ready states ---- */

  if (phase === "loading") {
    return (
      <section className="awprod">
        <div className="awprod-note">
          <h1>Reading the tag</h1>
          <p>Pulling everything the watcher knows about this product.</p>
        </div>
      </section>
    );
  }

  if (phase === "missing") {
    return (
      <section className="awprod">
        <div className="awprod-note">
          <h1>No such product</h1>
          <p>
            The watcher has never recorded this id. It may have been removed before the first crawl.{" "}
            <Link to="/">Back to the stack</Link>.
          </p>
        </div>
      </section>
    );
  }

  if (phase === "error" || !detail || !product) {
    return (
      <section className="awprod">
        <div className="awprod-note">
          <h1>Could not load</h1>
          <p>{error || "The watcher did not answer."}</p>
          <button type="button" className="awprod-btn" onClick={() => setReloadNonce((n) => n + 1)}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  const shot = gallery.length > 0 ? gallery[Math.min(activeShot, gallery.length - 1)] : undefined;
  const statusAlert = product.status === "unlisted" || product.status === "gone" || product.status === "feed-only";
  const priceRange =
    product.priceMinCents !== product.priceMaxCents
      ? `${money(product.priceMinCents)} – ${money(product.priceMaxCents)}`
      : money(product.priceCents);

  return (
    <section className="awprod">
      <Link className="awprod-back" to="/">
        &larr; Shop
      </Link>

      <div className="awprod-hero">
        <div className="awprod-media">
          {shot ? (
            <img className="awprod-shot" src={shot} alt={product.title} />
          ) : (
            <div className="awprod-noshot">No image recorded</div>
          )}

          {gallery.length > 1 ? (
            <div className="awprod-thumbs">
              {gallery.map((src, index) => (
                <button
                  key={src}
                  type="button"
                  className={`awprod-thumb${index === Math.min(activeShot, gallery.length - 1) ? " is-on" : ""}`}
                  onClick={() => setActiveShot(index)}
                  aria-label={`View image ${index + 1}`}
                >
                  <img src={src} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="awprod-tagwrap">
          <TagString />
          <div className="awprod-tag">
            <span className="awprod-hole" aria-hidden="true" />

            <p className="awprod-price">{priceRange}</p>
            <h1 className="awprod-name">{product.title}</h1>

            {product.descriptionLines.length > 0 ? (
              <ul className="awprod-desc">
                {product.descriptionLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}

            {sizes.length > 0 ? (
              <ul className="awprod-sizes">
                {sizes.map((size) => {
                  const out = !product.availableSizes.includes(size);
                  const picked = pickedSizes.includes(size);
                  const className = `awprod-size${out ? " is-out" : ""}${picked ? " is-picked" : ""}`;
                  return (
                    <li key={size}>
                      {canWatch ? (
                        <button
                          type="button"
                          className={className}
                          onClick={() => toggleSize(size)}
                          aria-pressed={picked}
                          title={
                            picked ? `Stop watching ${size}` : `Watch ${size}${out ? " for a restock" : ""}`
                          }
                        >
                          {size}
                        </button>
                      ) : (
                        <span className={className}>{size}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : null}

            <p className="awprod-guide">Size guide</p>

            {!product.available ? <p className="awprod-soldout">Sold out</p> : null}

            {canWatch ? (
              <>
                <button
                  type="button"
                  className={`awprod-watch${watch ? " is-on" : ""}`}
                  onClick={() => (watch ? void stopWatch() : void saveWatch(pickedSizes))}
                  disabled={watchBusy}
                >
                  {watchBusy ? "…" : watch ? "Watching" : "Watch"}
                </button>
                <p className={`awprod-hint${watchError ? " is-error" : ""}`}>
                  {watchError
                    ? watchError
                    : watch
                      ? `${pickedSizes.length > 0 ? pickedSizes.join(" · ") : "Any size"} — tap to stop`
                      : pickedSizes.length > 0
                        ? `Alerts for ${pickedSizes.join(" · ")}`
                        : "Alerts for any size"}
                </p>
              </>
            ) : null}

            <p className="awprod-hint">
              {restockTotal > 0 ? `Restocked ${restockTotal}× · ` : ""}
              Last seen {relativeTime(product.lastSeen)}
            </p>

            {liveTick > 0 ? (
              <span key={liveTick} className="awprod-flash">
                Updated live
              </span>
            ) : null}

            <Swirl />
            <p className="awprod-fine">No return or exchanges</p>
          </div>
        </div>
      </div>

      <div className={`awprod-status${statusAlert ? " is-alert" : ""}`}>
        <span className="awprod-statusword">{STATUS_LABEL[product.status] ?? product.status}</span>
        <p className="awprod-statusnote">{STATUS_NOTE[product.status] ?? "Status unrecognised."}</p>
      </div>

      <div className="awprod-panels">
        <div className="awprod-panel">
          <h2>Trace</h2>
          <dl className="awprod-facts">
            <Fact label="First seen" iso={product.firstSeen} />
            <Fact label="Last seen" iso={product.lastSeen} />
            <Fact label="Published" iso={product.publishedAt} fallback="not published" />
            <Fact label="First available" iso={product.firstAvailableAt} fallback="never available" />
            <Fact label="Last available" iso={product.lastAvailableAt} fallback="never available" />
            <Fact label="Sold out at" iso={product.soldOutAt} fallback="still in stock" />
            <dt>Handle</dt>
            <dd className="awprod-path">{product.handle}</dd>
            <dt>Vendor</dt>
            <dd>{product.vendor || "—"}</dd>
            <dt>Sources</dt>
            <dd>
              {product.inFeed ? "products feed" : "not in feed"} · {product.inMarkup ? "page markup" : "not in markup"}
            </dd>
            <dt>Variants</dt>
            <dd>
              {product.availableVariants} of {product.totalVariants} available
            </dd>
          </dl>
        </div>

        <div className="awprod-panel">
          <h2>Price</h2>
          <dl className="awprod-facts">
            <dt>Current</dt>
            <dd>{money(product.priceCents)}</dd>
            {product.priceMinCents !== product.priceMaxCents ? (
              <>
                <dt>Range</dt>
                <dd>
                  {money(product.priceMinCents)} – {money(product.priceMaxCents)}
                </dd>
              </>
            ) : null}
          </dl>

          {priceHistory.length > 0 ? (
            <ul className="awprod-rows" style={{ marginTop: 14 }}>
              {priceHistory.map((event) => (
                <li key={event._id}>
                  <div className="awprod-rowtop">
                    <span>
                      {formatValue(event.type, event.fromValue)} &rarr; {formatValue(event.type, event.toValue)}
                    </span>
                    <span className="awprod-rowtime" title={fullStamp(event.occurredAt)}>
                      {relativeTime(event.occurredAt)}
                    </span>
                  </div>
                  {event.detail ? <p className="awprod-rowsub">{event.detail}</p> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="awprod-empty" style={{ marginTop: 14 }}>
              The price has not moved since the watcher first saw this product.
            </p>
          )}
        </div>

        <div className="awprod-panel is-wide">
          <h2>Stock by size</h2>
          <StockTimeline variants={detail.variants} events={detail.events} />
        </div>

        <div className="awprod-panel is-wide">
          <h2>Placement</h2>
          {placements.length > 0 ? (
            <ul className="awprod-rows">
              {placements.map((placement) => (
                <li key={`${placement.path}::${placement.scopeKey}::${placement.pageIndex}`}>
                  <div className="awprod-rowtop">
                    <span className="awprod-path">
                      {placement.page?.url ? (
                        <a href={placement.page.url} target="_blank" rel="noreferrer noopener">
                          {placement.path}
                        </a>
                      ) : (
                        placement.path
                      )}
                      {placement.page?.orphaned ? <span className="awprod-flag">Orphan</span> : null}
                      {placement.page?.gone ? <span className="awprod-flag">Gone</span> : null}
                    </span>
                    <span className="awprod-rowtime" title={fullStamp(placement.lastSeen)}>
                      {relativeTime(placement.lastSeen)}
                    </span>
                  </div>
                  <p className="awprod-rowsub">
                    Position {placement.pageIndex + 1} on the page · scope {placement.scopeKey}
                    {placement.page?.role ? ` · ${placement.page.role}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="awprod-empty">
              Not placed on any crawled page. It exists in the feed but nothing on the site links to it.
            </p>
          )}
        </div>

        <div className="awprod-panel is-wide is-flush">
          <h2>Activity</h2>
          {detail.events.length > 0 ? (
            <ul className="awprod-eventlist">
              {detail.events.map((event) => (
                <EventRow key={event._id} event={event} />
              ))}
            </ul>
          ) : (
            <p className="awprod-empty" style={{ padding: "0 22px 22px" }}>
              Nothing has happened to this product since the watcher started tracking it.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export default Product;
